import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { useEffect } from 'react';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { BOTPRESS_PROVIDER_ID, IFRAME_QUERY_SELECTOR, OBSERVER_TIMEOUT, MESSAGE_CONTAINER_SELECTOR, MAX_OBSERVER_RETRIES, MAX_IFRAME_RETRIES, EXTERNAL_WINDOW_TARGET, EXTERNAL_WINDOW_REL, BOTPRESS_SCRIPT_NAME } from './BotpressProvider.constants';
import { createConfig, createLinkObserver, onRemoveBotpress } from './BotpressProvider.utils';
import BotpressNotification from './components/BotpressNotification';
import Script from '../../../../components/Script';
import { isServerSideRendering } from '../../../../utils/gatsby';
import { BOTPRESS_GOOGLE_ANALYTICS_EVENT, BOTPRESS_PROVIDER_LOADED_EVENT, BOTPRESS_WEBCHAT_CLOSED_EVENT, BOTPRESS_WEBCHAT_OPENED_EVENT, BOTPRESS_CONVERSATION_EVENT, BOTPRESS_PROVIDER_CONVERSATION_EVENT } from '../../constants';
import useBotpress from '../../hooks/useBotpress';
import { isBotpressInitialized, isBotpressLoaded } from '../../services/checks';
import { generateBotpressNavigationWebEvent } from '../../services/eventGenerators';
import { getEventType, processGoogleAnalyticsEvent } from '../../services/eventProcessors';
import { BotpressConfigPropType } from '../../types/botpress';
var defaultProps = {
  botpressSrc: 'https://cdn.botpress.cloud/webchat/v2.2/inject.js'
};
export default function BotpressProvider(_ref) {
  var botpressSrc = _ref.botpressSrc,
    botpressConfig = _ref.botpressConfig,
    botId = _ref.botId,
    clientId = _ref.clientId,
    merchantId = _ref.merchantId;
  if (isServerSideRendering()) return null;
  var _useBotpress = useBotpress(),
    hideNotifications = _useBotpress.hideNotifications,
    showNotifications = _useBotpress.showNotifications,
    dismissAllNotifications = _useBotpress.dismissAllNotifications,
    sendEvent = _useBotpress.sendEvent;
  var location = useLocation();
  function handleConversationReady() {
    window.dispatchEvent(new CustomEvent(BOTPRESS_PROVIDER_CONVERSATION_EVENT, {}));
    window.botpress.conversationReady = true;
  }
  function handleOpenChat() {
    hideNotifications();
  }
  function handleCloseChat() {
    dismissAllNotifications();
    showNotifications();
  }
  function handleBotpressEvent(event) {
    var _eventHandlerMap;
    var eventType = getEventType(event);
    var eventHandlerMap = (_eventHandlerMap = {}, _defineProperty(_eventHandlerMap, BOTPRESS_WEBCHAT_OPENED_EVENT, handleOpenChat), _defineProperty(_eventHandlerMap, BOTPRESS_WEBCHAT_CLOSED_EVENT, handleCloseChat), _defineProperty(_eventHandlerMap, BOTPRESS_GOOGLE_ANALYTICS_EVENT, processGoogleAnalyticsEvent), _defineProperty(_eventHandlerMap, BOTPRESS_CONVERSATION_EVENT, handleConversationReady), _eventHandlerMap);
    var handler = eventHandlerMap[eventType];
    if (handler) {
      handler(event);
    }
  }
  function handleLinkNavigation(isExternalLink, href) {
    if (isExternalLink) {
      var newWindow = window.open(href, EXTERNAL_WINDOW_TARGET);
      if (newWindow) {
        newWindow.opener = null;
        newWindow.rel = EXTERNAL_WINDOW_REL;
      }
    } else {
      navigate(href);
    }
  }
  function handleScriptLoad() {
    if (!isBotpressLoaded()) return;
    var configuration = createConfig(botpressConfig);
    if (!isBotpressInitialized()) {
      window.botpress.init({
        botId: botId,
        configuration: configuration,
        clientId: clientId,
        user: {
          data: {
            merchantId: merchantId
          }
        }
      });
    }
    if (isBotpressInitialized()) {
      window.botpress.on('*', handleBotpressEvent);
      window.dispatchEvent(new Event(BOTPRESS_PROVIDER_LOADED_EVENT));
    }
  }
  useEffect(function () {
    // When location changes, run actions
    sendEvent(generateBotpressNavigationWebEvent(location));
  }, [location.pathname]);
  useEffect(function () {
    // when initially loaded run actions
    var observer = createLinkObserver(handleLinkNavigation);
    var iframeListenerRef;
    function observeIframeContent(iframe) {
      var retryCount = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      try {
        var iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
        var messageContainer = iframeDocument === null || iframeDocument === void 0 ? void 0 : iframeDocument.querySelector(MESSAGE_CONTAINER_SELECTOR);
        if (messageContainer) {
          observer.observe(messageContainer, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true
          });
        } else if (retryCount < MAX_IFRAME_RETRIES) {
          setTimeout(function () {
            return observeIframeContent(iframe, retryCount + 1);
          }, OBSERVER_TIMEOUT);
        }
      } catch (error) {
        console.warn('BotpressProvider Error accessing iframe ', error);
      }
    }
    function initializeIframeObserver() {
      var retryCount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var webchatIframe = document.querySelector(IFRAME_QUERY_SELECTOR);
      if (!webchatIframe) {
        if (retryCount < MAX_OBSERVER_RETRIES) {
          setTimeout(function () {
            return initializeIframeObserver(retryCount + 1);
          }, OBSERVER_TIMEOUT);
        }
        return;
      }
      observeIframeContent(webchatIframe);
      webchatIframe.addEventListener('load', function () {
        return observeIframeContent(webchatIframe);
      });
      iframeListenerRef = function iframeListenerRef() {
        webchatIframe.removeEventListener('load', observeIframeContent);
      };
    }
    if (isBotpressInitialized()) {
      initializeIframeObserver();
    } else {
      window.addEventListener(BOTPRESS_PROVIDER_LOADED_EVENT, initializeIframeObserver);
    }
    return function () {
      if (iframeListenerRef) iframeListenerRef();
      observer.disconnect();
    };
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    id: BOTPRESS_PROVIDER_ID
  }, /*#__PURE__*/React.createElement(BotpressNotification, null), /*#__PURE__*/React.createElement(Script, {
    id: BOTPRESS_SCRIPT_NAME,
    src: botpressSrc,
    onLoad: handleScriptLoad,
    onRemove: onRemoveBotpress,
    strategy: Script.STRATEGY_AFTER_USER_INTERACTION
  }));
}
BotpressProvider.defaultProps = defaultProps;